import React, { useEffect, useState, useMemo } from "react";
import { InputField } from "./InputField";
import { AnimatePresence, motion } from "framer-motion";
import { useNotification } from "./NotificationContext";
import { db } from "@lib/firebase";
import {
  doc,
  setDoc,
  getDoc,
  query,
  collection,
  where,
  getDocs,
} from "firebase/firestore";
import { serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";

// Fungsi untuk menguraikan format tanggal khusus
const parseCustomDate = (dateString) => {
  try {
    const regex =
      /^(\d{1,2})\s(\w+)\s(\d{4})\sat\s(\d{2}:\d{2}:\d{2})\sUTC\+7$/;
    const match = dateString.match(regex);
    if (!match) {
      console.error("[ERROR] Invalid date format:", dateString);
      return null;
    }

    const [, day, monthStr, year, time] = match;
    const monthMap = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };
    const month = monthMap[monthStr];
    if (!month) {
      console.error("[ERROR] Invalid month in date string:", monthStr);
      return null;
    }

    const isoDateString = `${year}-${month}-${day.padStart(
      2,
      "0"
    )}T${time}+07:00`;
    const parsedDate = new Date(isoDateString);
    if (isNaN(parsedDate.getTime())) {
      console.error("[ERROR] Failed to parse date:", isoDateString);
      return null;
    }
    console.log("[DEBUG] Successfully parsed date:", {
      input: dateString,
      output: parsedDate,
    });
    return parsedDate;
  } catch (err) {
    console.error("[ERROR] Error parsing date:", err, dateString);
    return null;
  }
};

export const InputFieldParticipants = ({
  formData,
  handleChange,
  isFormDisabled = false,
  labelColor,
  eventId,
  categoryName,
  updateUserData,
  categories = [],
}) => {
  const { showNotification } = useNotification();
  const memoizedShowNotification = useMemo(
    () => showNotification,
    [showNotification]
  );
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [rawCountries, setRawCountries] = useState([]);
  const [provinceIds, setProvinceIds] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState({
    countries: false,
    provinces: false,
    cities: false,
    bibNumber: false,
    categories: false,
  });
  const [participantProvinces, setParticipantProvinces] = useState([
    [],
    [],
    [],
  ]);
  const [participantCities, setParticipantCities] = useState([[], [], []]);
  const [participantProvinceIds, setParticipantProvinceIds] = useState([
    {},
    {},
    {},
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentParticipantIndex, setCurrentParticipantIndex] = useState(0);
  const [genderError, setGenderError] = useState("");
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [localCategories, setLocalCategories] = useState([]);
  const [hasFetchedCategories, setHasFetchedCategories] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const geoNamesUsername = "xtrifeinnie";
  const sizeChartUrl = "/jersey/sizechart.jpg";
  const cache = useMemo(() => new Map(), []);
  const isGroupRegistration =
    eventId === "JOTR" && categoryName?.includes("Group");

  // Cek status admin
  useEffect(() => {
    const auth = getAuth();
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult();
          const isAdminUser = idTokenResult.claims.role === "admin";
          console.log("[DEBUG] Admin status:", isAdminUser);
          setIsAdmin(isAdminUser);
        } catch (err) {
          console.error("[ERROR] Error checking admin status:", err);
          setIsAdmin(false);
        }
      }
    };
    checkAdminStatus();
  }, []);

  // Debounce function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(null, args), wait);
    };
  };

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "Nomor Identitas";
    if (str === "bibNumber") return "BIB Number";
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Fetch categories from Firestore if not provided
  useEffect(() => {
    if (
      hasFetchedCategories ||
      categories.length > 0 ||
      localCategories.length > 0
    ) {
      console.log("[DEBUG] Categories prop received:", categories);
      if (categories.length > 0 && localCategories.length === 0) {
        console.log("[DEBUG] Using categories from props:", categories);
        setLocalCategories(categories);
      }
      return;
    }

    console.log(
      "[DEBUG] Categories prop is empty. Attempting to fetch from Firestore."
    );
    const fetchCategories = async () => {
      setLoading((prev) => ({ ...prev, categories: true }));
      try {
        const eventDocRef = doc(db, "semuaKegiatan", "JOTR");
        const eventDoc = await getDoc(eventDocRef);
        if (eventDoc.exists()) {
          const eventData = eventDoc.data();
          console.log("[DEBUG] Raw Firestore event data:", eventData);
          if (!eventData.categories || !Array.isArray(eventData.categories)) {
            console.error(
              "[ERROR] Firestore categories missing or not an array:",
              eventData.categories
            );
            memoizedShowNotification(
              "error",
              "Category data not found on server. Please contact admin."
            );
            setLocalCategories([]);
          } else {
            const fetchedCategories = eventData.categories.map((cat) => ({
              categoryName: cat.categoryName,
              date: cat.date,
              cot: cat.cot,
              price: cat.price,
              eventDateTimestamp: cat.eventDateTimestamp,
            }));
            setLocalCategories(fetchedCategories);
            console.log(
              "[DEBUG] Fetched categories from Firestore:",
              fetchedCategories.map((cat) => cat.categoryName)
            );
          }
        } else {
          console.error(
            "[ERROR] Event document JOTR not found in semuaKegiatan."
          );
          memoizedShowNotification(
            "error",
            "JOTR event document not found on server. Please contact admin."
          );
          setLocalCategories([]);
        }
      } catch (err) {
        console.error("[ERROR] Error fetching categories:", err);
        memoizedShowNotification(
          "error",
          "Failed to load category data from server: " + err.message
        );
        setLocalCategories([]);
      } finally {
        setLoading((prev) => ({ ...prev, categories: false }));
        setHasFetchedCategories(true);
      }
    };

    fetchCategories();
  }, [
    memoizedShowNotification,
    hasFetchedCategories,
    categories,
    localCategories,
  ]);

  // Fetch registration data
  useEffect(() => {
    if (!isGroupRegistration || isDraftLoaded) return;

    const fetchRegistrationData = async () => {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      if (!userId || !eventId) {
        console.error(
          "[DEBUG] Missing userId or eventId for fetching registration data"
        );
        memoizedShowNotification(
          "error",
          "User or event information is incomplete."
        );
        return;
      }

      try {
        const docRef = doc(db, "registrations", `${userId}_${eventId}`);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log(
            "[DEBUG] Fetched registration data from Firestore:",
            data
          );

          const defaultParticipant = {
            namaDepan: "",
            namaBelakang: "",
            bibName: "",
            bibNumber: "",
            telepon: "",
            nomorIdentitas: "",
            tanggalLahir: "",
            golonganDarah: "",
            sizeJersey: "",
            gender: "",
            negara: "Indonesia",
            provinsi: "",
            kota: "",
            alamat: "",
            kontakDarurat: "",
            namaKontakDarurat: "",
            hubunganKontakDarurat: "",
            performanceIndex: "",
            stravaProfile: "",
          };

          const participants = Array(3)
            .fill()
            .map((_, index) => ({
              ...defaultParticipant,
              ...(data.participants && data.participants[index]
                ? data.participants[index]
                : {}),
            }));

          handleChange({
            target: {
              name: "participants",
              value: participants,
            },
          });

          if (data.teamName) {
            handleChange({
              target: {
                name: "teamName",
                value: data.teamName,
              },
            });
          }

          if (data.kategori) {
            handleChange({
              target: {
                name: "kategori",
                value: data.kategori,
              },
            });
          }

          console.log("[DEBUG] Applied registration data to formData:", {
            participants,
            teamName: data.teamName,
            kategori: data.kategori,
          });
        } else {
          console.log(
            "[DEBUG] No registration data found in Firestore, using default formData."
          );
        }
      } catch (err) {
        console.error("[ERROR] Error fetching registration data:", err);
        memoizedShowNotification(
          "error",
          "Failed to load registration data from server."
        );
      } finally {
        setIsDraftLoaded(true);
      }
    };

    fetchRegistrationData();
  }, [
    eventId,
    isGroupRegistration,
    handleChange,
    memoizedShowNotification,
    isDraftLoaded,
  ]);

  const fetchWithCache = async (
    url,
    cacheKey,
    type,
    participantIndex = null
  ) => {
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.geonames) {
        cache.set(cacheKey, data);
        return data;
      } else {
        setErrors((prev) => ({
          ...prev,
          [participantIndex !== null ? `${type}_${participantIndex}` : type]:
            "Gagal memuat data lokasi. Silakan coba lagi nanti.",
        }));
        memoizedShowNotification(
          "error",
          "Failed to load location data. Please try again later."
        );
        return null;
      }
    } catch (err) {
      console.error(`[ERROR] Error fetching ${cacheKey}:`, err);
      setErrors((prev) => ({
        ...prev,
        [participantIndex !== null ? `${type}_${participantIndex}` : type]:
          "Gagal memuat data lokasi. Silakan coba lagi nanti.",
      }));
      memoizedShowNotification(
        "error",
        "Failed to load location data. Please try again later."
      );
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  const checkBibNumberUniqueness = async (
    bibNumber,
    participantIndex = null
  ) => {
    if (!bibNumber) return true;
    setLoading((prev) => ({ ...prev, bibNumber: true }));
    try {
      const q = query(
        collection(db, "pendaftaranJOTR"),
        where("bibNumber", "==", bibNumber)
      );
      const querySnapshot = await getDocs(q);
      const isUsed = !querySnapshot.empty;
      if (isUsed) {
        setErrors((prev) => ({
          ...prev,
          [participantIndex !== null
            ? `bibNumber_${participantIndex}`
            : "bibNumber"]: `BIB Number ${bibNumber} is already used.`,
        }));
        return false;
      }
      return true;
    } catch (err) {
      console.error("[ERROR] Error checking BIB Number uniqueness:", err);
      setErrors((prev) => ({
        ...prev,
        [participantIndex !== null
          ? `bibNumber_${participantIndex}`
          : "bibNumber"]: "Gagal memeriksa BIB Number. Silakan coba lagi.",
      }));
      memoizedShowNotification(
        "error",
        "Failed to check BIB Number. Please try again."
      );
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, bibNumber: false }));
    }
  };

  const debouncedCheckBibNumberUniqueness = useMemo(
    () => debounce(checkBibNumberUniqueness, 500),
    []
  );

  useEffect(() => {
    fetchWithCache(
      `https://secure.geonames.org/countryInfoJSON?username=${geoNamesUsername}`,
      "countries",
      "countries"
    ).then((data) => {
      if (data?.geonames) {
        const countryList = data.geonames.map((country) => ({
          value: country.countryName,
          label: country.countryName,
        }));
        setCountries(countryList);
        setRawCountries(data.geonames);
      } else {
        setCountries([]);
        setRawCountries([]);
      }
    });
  }, [cache]);

  useEffect(() => {
    if (formData.negara && rawCountries.length > 0 && !isGroupRegistration) {
      setProvinces([]);
      setCities([]);
      const selectedCountry = rawCountries.find(
        (c) => c.countryName === formData.negara
      );
      if (!selectedCountry) return;

      fetchWithCache(
        `https://secure.geonames.org/childrenJSON?geonameId=${selectedCountry.geonameId}&username=${geoNamesUsername}&lang=id`,
        `provinces_${selectedCountry.geonameId}`,
        "provinces"
      ).then((data) => {
        if (data?.geonames) {
          const provinceList = data.geonames.map((prov) => ({
            value: prov.name,
            label: prov.name,
          }));
          const idMap = {};
          data.geonames.forEach((prov) => {
            idMap[prov.name] = prov.geonameId;
          });
          setProvinces(provinceList);
          setProvinceIds(idMap);
        }
      });
    }
  }, [formData.negara, rawCountries, isGroupRegistration, cache]);

  useEffect(() => {
    if (
      formData.provinsi &&
      provinceIds[formData.provinsi] &&
      !isGroupRegistration
    ) {
      setCities([]);
      const provinceId = provinceIds[formData.provinsi];

      fetchWithCache(
        `https://secure.geonames.org/childrenJSON?geonameId=${provinceId}&username=${geoNamesUsername}&lang=id`,
        `cities_${provinceId}`,
        "cities"
      ).then((data) => {
        if (data?.geonames) {
          const cityList = data.geonames.map((city) => ({
            value: city.name,
            label: city.name,
          }));
          setCities(cityList);
        }
      });
    }
  }, [formData.provinsi, provinceIds, isGroupRegistration, cache]);

  useEffect(() => {
    if (isGroupRegistration && rawCountries.length > 0) {
      const participant = formData.participants[currentParticipantIndex];
      if (participant?.negara) {
        const selectedCountry = rawCountries.find(
          (c) => c.countryName === participant.negara
        );
        if (!selectedCountry) return;

        fetchWithCache(
          `https://secure.geonames.org/childrenJSON?geonameId=${selectedCountry.geonameId}&username=${geoNamesUsername}&lang=id`,
          `provinces_${selectedCountry.geonameId}_${currentParticipantIndex}`,
          "provinces",
          currentParticipantIndex
        ).then((data) => {
          if (data?.geonames) {
            const provinceList = data.geonames.map((prov) => ({
              value: prov.name,
              label: prov.name,
            }));
            const idMap = {};
            data.geonames.forEach((prov) => {
              idMap[prov.name] = prov.geonameId;
            });
            setParticipantProvinces((prev) => {
              const newProvinces = [...prev];
              newProvinces[currentParticipantIndex] = provinceList;
              return newProvinces;
            });
            setParticipantProvinceIds((prev) => {
              const newIds = [...prev];
              newIds[currentParticipantIndex] = idMap;
              return newIds;
            });

            if (participant.provinsi && idMap[participant.provinsi]) {
              fetchWithCache(
                `https://secure.geonames.org/childrenJSON?geonameId=${
                  idMap[participant.provinsi]
                }&username=${geoNamesUsername}&lang=id`,
                `cities_${
                  idMap[participant.provinsi]
                }_${currentParticipantIndex}`,
                "cities",
                currentParticipantIndex
              ).then((data) => {
                if (data?.geonames) {
                  const cityList = data.geonames.map((city) => ({
                    value: city.name,
                    label: city.name,
                  }));
                  setParticipantCities((prev) => {
                    const newCities = [...prev];
                    newCities[currentParticipantIndex] = cityList;
                    return newCities;
                  });
                }
              });
            }
          }
        });
      }
    }
  }, [
    formData.participants,
    rawCountries,
    isGroupRegistration,
    currentParticipantIndex,
    cache,
  ]);

  const validateField = (
    name,
    value,
    participantIndex = null,
    negara = "Indonesia"
  ) => {
    const label = humanize(name);

    if (
      name === "bibNumber" &&
      (value === undefined || value === null || value === "")
    ) {
      return "";
    }

    if (
      name !== "bibNumber" &&
      (value === undefined || value === null || value === "")
    ) {
      return `${label} is required`;
    }

    if (name === "teamName" && value) {
      if (!/^[A-Za-z0-9\s]{3,50}$/.test(value)) {
        return `${label} must be 3-50 characters, alphanumeric with spaces, e.g., "Fast Run Team"`;
      }
    }
    if (name === "namaDepan" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} must be letters and spaces only, max 50 characters, e.g., "Budi"`;
      }
    }
    if (name === "namaBelakang" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} must be letters and spaces only, max 50 characters, e.g., "Santoso"`;
      }
    }
    if (name === "bibName" && value) {
      if (!/^[A-Za-z0-9\s]{1,20}$/.test(value)) {
        return `${label} must be 1-20 alphanumeric characters and spaces, e.g., "BUDI123"`;
      }
    }
    if (name === "bibNumber" && value) {
      if (!/^\d{1,4}$/.test(value)) {
        return `${label} must be 1-4 digits, e.g., "1234"`;
      }
    }
    if (name === "telepon" && value) {
      if (!/^\+?\d{8,15}$/.test(value)) {
        return `${label} harus 8-15 digit, boleh diawali dengan "+", misalnya: "+6281234567890"`;
      }
    }
    if (name === "nomorIdentitas" && value) {
      if (negara === "Indonesia") {
        if (!/^\d{16}$/.test(value)) {
          return `${label} (KTP) must be 16 digits for Indonesian participants, e.g., "1234567890123456"`;
        }
      } else {
        if (!/^[A-Za-z0-9]{5,20}$/.test(value)) {
          return `${label} (Passport/ID) must be 5-20 alphanumeric characters for foreign participants, e.g., "AB123456"`;
        }
      }
    }
    if (name === "tanggalLahir" && value) {
      const date = new Date(value);
      const eventDate = new Date("2025-09-27");
      const minAgeDate = new Date(
        eventDate.getFullYear() - 15,
        eventDate.getMonth(),
        eventDate.getDate()
      );
      if (isNaN(date.getTime()) || date >= eventDate) {
        return `${label} must be in the past before 27/9/2025, e.g., "2010-09-27"`;
      }
      if (date > minAgeDate) {
        return `Participant must be at least 15 years old on 27/9/2025, e.g., "2010-09-27" or earlier`;
      }
    }
    if (name === "golonganDarah" && value) {
      if (!["A", "B", "AB", "O"].includes(value)) {
        return `${label} must be A, B, AB, or O`;
      }
    }
    if (name === "sizeJersey" && value) {
      if (!["XS", "S", "M", "L", "XL", "XXL"].includes(value)) {
        return `${label} must be XS, S, M, L, XL, or XXL`;
      }
    }
    if (name === "gender" && value) {
      if (!["Laki-laki", "Perempuan"].includes(value)) {
        return `${label} must be Male or Female`;
      }
    }
    if (name === "negara" && value) {
      if (countries.length > 0 && !countries.some((c) => c.value === value)) {
        return `${label} must be selected from the available list, e.g., "Indonesia"`;
      }
    }
    if (name === "provinsi" && value) {
      const provinceList =
        participantIndex !== null
          ? participantProvinces[participantIndex]
          : provinces;
      if (
        provinceList.length > 0 &&
        !provinceList.some((p) => p.value === value)
      ) {
        return `${label} must be selected from the available list, e.g., "West Java"`;
      }
    }
    if (name === "kota" && value) {
      const cityList =
        participantIndex !== null
          ? participantCities[participantIndex]
          : cities;
      if (cityList.length > 0 && !cityList.some((c) => c.value === value)) {
        return `${label} must be selected from the available list, e.g., "Bandung"`;
      }
    }
    if (name === "alamat" && value) {
      if (!/^[A-Za-z0-9\s,.-/#()]{5,100}$/.test(value)) {
        return `${label} must be 5-100 characters, letters, numbers, spaces, commas, dots, hyphens, slashes, hashes, or parentheses allowed, e.g., "Jalan Merdeka No. 123"`;
      }
    }
    if (name === "kontakDarurat" && value) {
      if (!/^\+?\d{8,15}$/.test(value)) {
        return `${label} harus 8-15 digit, boleh diawali dengan "+", misalnya: "+6281234567890"`;
      }
    }
    if (name === "namaKontakDarurat" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} must be letters and spaces only, max 50 characters, e.g., "Ani"`;
      }
    }
    if (name === "hubunganKontakDarurat" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} must be letters and spaces only, max 50 characters, e.g., "Wife"`;
      }
    }
    if (name === "performanceIndex" && value) {
      if (!/^\d{3}$/.test(value)) {
        return `${label} must be 3 digits, e.g., "460"`;
      }
    }
    if (name === "stravaProfile" && value) {
      return "";
    }
    if (name === "kategori" && value) {
      console.log("[DEBUG] Validating category:", {
        selected: value,
        availableCategories: localCategories.map((cat) => cat.categoryName),
        localCategories,
      });
      if (localCategories.length === 0) {
        console.log("[DEBUG] No categories available for validation.");
        return `${label} cannot be validated because category data is not yet available.`;
      }
      const isValidCategory = localCategories.some(
        (cat) => cat.categoryName === value
      );
      if (!isValidCategory) {
        console.log("[DEBUG] Category validation failed:", {
          selected: value,
          available: localCategories.map((cat) => cat.categoryName),
        });
        return `${label} is invalid. Select a category from the available list.`;
      }
      // Lewati semua validasi untuk admin
      if (isAdmin) {
        console.log("[DEBUG] Bypassing all category validation for admin:", {
          category: value,
        });
        return "";
      }
      // Validasi tanggal untuk non-admin
      const selectedCategory = localCategories.find(
        (cat) => cat.categoryName === value
      );
      if (selectedCategory) {
        const eventDate = parseCustomDate(selectedCategory.eventDateTimestamp);
        const currentDate = new Date();
        console.log("[DEBUG] Category date validation:", {
          category: value,
          eventDate,
          currentDate,
        });
        if (!eventDate || isNaN(eventDate.getTime())) {
          console.error(
            "[ERROR] Invalid eventDateTimestamp for category:",
            selectedCategory
          );
          return `${label} has an invalid date. Contact admin.`;
        }
        if (eventDate < currentDate) {
          console.log("[DEBUG] Category registration closed:", {
            category: value,
            eventDate,
            currentDate,
          });
          return `${label} registration for ${value} is closed.`;
        }
      }
      return "";
    }
    return "";
  };

  const validateIndividual = () => {
    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
      ...(eventId === "JOTR"
        ? [
            "bibName",
            "golonganDarah",
            "sizeJersey",
            "performanceIndex",
            "stravaProfile",
            "kategori",
          ]
        : []),
    ];
    const errors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = validateField(
        field,
        formData[field],
        null,
        formData.negara
      );
      if (error && touched[field]) {
        errors[field] = error;
        isValid = false;
      }
    });

    console.log("[DEBUG] validateIndividual errors:", errors);
    return { isValid, errors };
  };

  const validateParticipant = (participantData, index) => {
    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "bibName",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "golonganDarah",
      "sizeJersey",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
      "performanceIndex",
      "stravaProfile",
    ];
    const errors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = validateField(
        field,
        participantData[field],
        index,
        participantData.negara
      );
      if (error) {
        errors[`${field}_${index}`] = error;
        isValid = false;
      }
    });

    return { isValid, errors };
  };

  const validateAllParticipants = async () => {
    let allValid = true;
    let allErrors = {};
    const bibNames = new Set();
    const bibNumbers = new Set();

    setTouched((prev) => {
      const newTouched = { ...prev };
      formData.participants.forEach((participant, index) => {
        Object.keys(participant).forEach((field) => {
          newTouched[`${field}_${index}`] = true;
        });
      });
      return newTouched;
    });

    for (let index = 0; index < formData.participants.length; index++) {
      const participant = formData.participants[index];
      const { isValid, errors } = validateParticipant(participant, index);
      if (!isValid) {
        allValid = false;
        allErrors = { ...allErrors, ...errors };
      }
      if (participant.bibName) {
        if (bibNames.has(participant.bibName)) {
          allErrors[`bibName_${index}`] = `BIB Name is already used`;
          allValid = false;
        } else {
          bibNames.add(participant.bibName);
        }
      }
      if (participant.bibNumber) {
        if (bibNumbers.has(participant.bibNumber)) {
          allErrors[
            `bibNumber_${index}`
          ] = `BIB Number is already used in the team`;
          allValid = false;
        } else {
          const isUnique = await checkBibNumberUniqueness(
            participant.bibNumber,
            index
          );
          if (!isUnique) {
            allErrors[
              `bibNumber_${index}`
            ] = `BIB Number ${participant.bibNumber} is already used`;
            allValid = false;
          } else {
            bibNumbers.add(participant.bibNumber);
          }
        }
      }
    }

    const allMale = formData.participants.every(
      (p) => p.gender === "Laki-laki"
    );
    if (allMale) {
      setGenderError(
        "The team cannot consist of all males. At least one participant must be female."
      );
      allValid = false;
    } else {
      setGenderError("");
    }

    setErrors(allErrors);
    return allValid;
  };

  const handleFieldChange = (e, participantIndex = null) => {
    const { name, value } = e.target;
    console.log("[DEBUG] Field changed:", {
      name,
      value,
      participantIndex,
      formDataKategori: formData.kategori,
    });

    // Perbarui formData melalui handleChange
    handleChange(e, participantIndex);

    // Tandai field sebagai touched
    setTouched((prev) => ({
      ...prev,
      [participantIndex !== null ? `${name}_${participantIndex}` : name]: true,
    }));

    // Validasi field
    const error = validateField(
      name,
      value,
      participantIndex,
      participantIndex !== null
        ? formData.participants[participantIndex]?.negara
        : formData.negara
    );

    // Perbarui state errors
    setErrors((prev) => {
      const newErrors = { ...prev };
      const errorKey =
        participantIndex !== null ? `${name}_${participantIndex}` : name;
      if (error) {
        newErrors[errorKey] = error;
      } else {
        delete newErrors[errorKey];
      }
      console.log("[DEBUG] Updated errors after field change:", newErrors);
      return newErrors;
    });

    // Hanya tampilkan notifikasi error jika ada error dan bukan admin untuk kategori
    if (error && !(name === "kategori" && isAdmin)) {
      console.log("[DEBUG] Showing notification for error:", {
        error,
        name,
        value,
      });
      memoizedShowNotification("error", error);
    } else if (name === "kategori" && isAdmin) {
      console.log("[DEBUG] Clearing category error for admin:", {
        name,
        value,
      });
      // Pastikan error kategori dihapus dari formData.errors
      handleChange({
        target: {
          name: "errors",
          value: { ...formData.errors, kategori: undefined },
        },
      });
    }

    // Log formData.errors untuk debugging
    console.log("[DEBUG] Current formData.errors:", formData.errors);
  };

  const handleBibNumberBlur = async (e, participantIndex = null) => {
    const { value } = e.target;
    if (value && !validateField("bibNumber", value, participantIndex)) {
      return;
    }
    if (value) {
      debouncedCheckBibNumberUniqueness(value, participantIndex);
    }
  };

  const saveToFirestore = async (showSuccessNotification = false) => {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId || !eventId) {
      console.error("[ERROR] Missing userId or eventId for Firestore save");
      memoizedShowNotification(
        "error",
        "Failed to save data: User or event information is incomplete."
      );
      return false;
    }

    try {
      const docRef = doc(db, "registrations", `${userId}_${eventId}`);
      const draftData = {
        participants: formData.participants,
        teamName: formData.teamName || "",
        eventId,
        userId,
        status: "draft",
        updatedAt: serverTimestamp(),
      };
      if (!isGroupRegistration && formData.bibNumber) {
        draftData.bibNumber = formData.bibNumber;
      }
      if (!isGroupRegistration && formData.kategori) {
        draftData.kategori = formData.kategori;
      }
      await setDoc(docRef, draftData);
      if (showSuccessNotification) {
        memoizedShowNotification(
          "success",
          "All participant data has been temporarily saved!"
        );
      }
      return true;
    } catch (err) {
      console.error("[ERROR] Error saving to Firestore:", err);
      memoizedShowNotification(
        "error",
        "Failed to save participant data to server."
      );
      return false;
    }
  };

  const handleNextParticipant = async () => {
    const currentParticipant = formData.participants[currentParticipantIndex];
    if (!currentParticipant) {
      console.error(
        "[ERROR] Current participant is undefined at index:",
        currentParticipantIndex
      );
      memoizedShowNotification(
        "error",
        `Participant data ${currentParticipantIndex + 1} not found.`
      );
      return;
    }
    const { isValid, errors: newErrors } = validateParticipant(
      currentParticipant,
      currentParticipantIndex
    );

    setTouched((prev) => {
      const newTouched = { ...prev };
      Object.keys(currentParticipant).forEach((field) => {
        newTouched[`${field}_${currentParticipantIndex}`] = true;
      });
      return newTouched;
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));

    if (!isValid) {
      const firstErrorKey = Object.keys(newErrors).find(
        (key) => newErrors[key]
      );
      const firstError = firstErrorKey
        ? newErrors[firstErrorKey]
        : "Please complete participant data correctly.";
      memoizedShowNotification("error", firstError);
      return;
    }

    if (currentParticipant.bibNumber) {
      const isUnique = await checkBibNumberUniqueness(
        currentParticipant.bibNumber,
        currentParticipantIndex
      );
      if (!isUnique) {
        memoizedShowNotification(
          "error",
          `BIB Number ${currentParticipant.bibNumber} is already used.`
        );
        return;
      }
    }

    const success = await saveToFirestore();
    if (!success) return;

    if (currentParticipantIndex === 0 && updateUserData) {
      const updatedFields = {
        namaDepan: currentParticipant.namaDepan,
        namaBelakang: currentParticipant.namaBelakang,
        namaLengkap:
          `${currentParticipant.namaDepan} ${currentParticipant.namaBelakang}`.trim(),
        telepon: currentParticipant.telepon,
        nomorIdentitas: currentParticipant.nomorIdentitas,
        tanggalLahir: currentParticipant.tanggalLahir,
        gender: currentParticipant.gender,
        negara: currentParticipant.negara,
        provinsi: currentParticipant.provinsi,
        kota: currentParticipant.kota,
        alamat: currentParticipant.alamat,
        kontakDarurat: currentParticipant.kontakDarurat,
        namaKontakDarurat: currentParticipant.namaKontakDarurat,
        hubunganKontakDarurat: currentParticipant.hubunganKontakDarurat,
        bibName: currentParticipant.bibName,
        bibNumber: currentParticipant.bibNumber || "",
        golonganDarah: currentParticipant.golonganDarah,
        sizeJersey: currentParticipant.sizeJersey,
        performanceIndex: currentParticipant.performanceIndex,
        stravaProfile: currentParticipant.stravaProfile,
      };

      try {
        await updateUserData(updatedFields);
      } catch (err) {
        console.error("[ERROR] Error updating user data:", err);
        memoizedShowNotification("error", "Failed to update user data.");
      }
    }

    if (currentParticipantIndex < 2) {
      setCurrentParticipantIndex(currentParticipantIndex + 1);
      setGenderError("");
    } else {
      const allValid = await validateAllParticipants();
      if (allValid) {
        await saveToFirestore(true);
        memoizedShowNotification(
          "success",
          "All participant data has been temporarily saved! Please proceed to submit."
        );
      } else {
        const firstErrorKey = Object.keys(errors).find((key) => errors[key]);
        const firstError = firstErrorKey
          ? errors[firstErrorKey]
          : genderError ||
            "There are errors in participant data. Please check again.";
        memoizedShowNotification("error", firstError);
      }
    }
  };

  const handlePreviousParticipant = () => {
    if (currentParticipantIndex > 0) {
      setCurrentParticipantIndex(currentParticipantIndex - 1);
      setGenderError("");
    }
  };

  const openModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(false);
  };

  const isNextDisabled = () => {
    if (
      !formData.participants ||
      !formData.participants[currentParticipantIndex]
    ) {
      return true;
    }
    const currentParticipant = formData.participants[currentParticipantIndex];
    const { isValid } = validateParticipant(
      currentParticipant,
      currentParticipantIndex
    );
    return !isValid || loading.bibNumber;
  };

  const getError = (field, participantIndex = null) => {
    const localError =
      participantIndex !== null
        ? errors[`${field}_${participantIndex}`]
        : errors[field];
    const formError =
      participantIndex !== null
        ? formData.errors?.[`${field}_${participantIndex}`]
        : formData.errors?.[field];
    return touched[field] ||
      (participantIndex !== null && touched[`${field}_${participantIndex}`])
      ? localError || formError
      : formError;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      {errors.countries && (
        <p className="text-red-500 col-span-full">{errors.countries}</p>
      )}

      {isGroupRegistration &&
      formData.participants &&
      formData.participants.length === 3 ? (
        <>
          <InputField
            label="Team Name"
            name="teamName"
            required
            value={formData.teamName || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            error={getError("teamName")}
            labelColor={labelColor}
          />
          <div className="col-span-full">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Team Participant Data (Must be 3 People, Minimum 1 Female)
            </h3>
            <div className="flex justify-center mb-4">
              {[1, 2, 3].map((step, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index === currentParticipantIndex
                        ? "bg-lime-600 text-white"
                        : "bg-slate-200 text-slate-800"
                    }`}
                  >
                    {step}
                  </div>
                  {index < 2 && (
                    <div className="w-8 h-1 bg-slate-200 mx-2"></div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {currentParticipantIndex === 0
                ? "Fill in your details"
                : currentParticipantIndex === 1
                ? "Fill in first member details"
                : "Fill in second member details"}
            </p>
            {genderError && <p className="text-red-500 mb-4">{genderError}</p>}
            <motion.div
              key={currentParticipantIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className=""
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="First Name"
                  name="namaDepan"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.namaDepan ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaDepan", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Last Name"
                  name="namaBelakang"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.namaBelakang || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaBelakang", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="BIB Name"
                  name="bibName"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.bibName ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("bibName", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <div className="relative">
                  <InputField
                    label="BIB Number (Optional)"
                    name="bibNumber"
                    value={
                      formData.participants[currentParticipantIndex]
                        ?.bibNumber || ""
                    }
                    onChange={(e) =>
                      handleFieldChange(e, currentParticipantIndex)
                    }
                    onBlur={(e) =>
                      handleBibNumberBlur(e, currentParticipantIndex)
                    }
                    disabled={isFormDisabled}
                    error={getError("bibNumber", currentParticipantIndex)}
                    labelColor={labelColor}
                    placeholder="Enter 1-4 digits, e.g., 1234"
                    maxLength="4"
                    aria-busy={loading.bibNumber}
                  />
                  {loading.bibNumber && (
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8 8 8 0 01-8-8z"
                        />
                      </svg>
                    </span>
                  )}
                </div>
                <InputField
                  label="Identity Number"
                  name="nomorIdentitas"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.nomorIdentitas || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("nomorIdentitas", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder={
                    formData.participants[currentParticipantIndex]?.negara ===
                    "Indonesia"
                      ? "Enter 16-digit ID Card (KTP)"
                      : "Enter Passport/ID Number"
                  }
                />
                <InputField
                  label="Date of Birth"
                  name="tanggalLahir"
                  type="date"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.tanggalLahir || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("tanggalLahir", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Blood Type"
                  name="golonganDarah"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.golonganDarah || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  options={[
                    { value: "", label: "Select Blood Type" },
                    { value: "A", label: "A" },
                    { value: "B", label: "B" },
                    { value: "AB", label: "AB" },
                    { value: "O", label: "O" },
                  ]}
                  error={getError("golonganDarah", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <div className="relative">
                  <InputField
                    label="Jersey Size"
                    name="sizeJersey"
                    type="select"
                    required
                    value={
                      formData.participants[currentParticipantIndex]
                        ?.sizeJersey || ""
                    }
                    onChange={(e) =>
                      handleFieldChange(e, currentParticipantIndex)
                    }
                    disabled={isFormDisabled}
                    options={[
                      { value: "", label: "Select Jersey Size" },
                      { value: "XS", label: "XS" },
                      { value: "S", label: "S" },
                      { value: "M", label: "M" },
                      { value: "L", label: "L" },
                      { value: "XL", label: "XL" },
                      { value: "XXL", label: "XXL" },
                    ]}
                    error={getError("sizeJersey", currentParticipantIndex)}
                    labelColor={labelColor}
                  />
                  <button
                    type="button"
                    onClick={openModal}
                    className="absolute top-0 right-0 mt-2 mr-2 text-lime-600 hover:text-lime-800 text-sm underline"
                    disabled={isFormDisabled}
                  >
                    View Size Chart
                  </button>
                </div>
                <InputField
                  label="Gender"
                  name="gender"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.gender || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  options={[
                    { value: "", label: "Select Gender" },
                    { value: "Laki-laki", label: "Male" },
                    { value: "Perempuan", label: "Female" },
                  ]}
                  error={getError("gender", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Phone Number/WhatsApp"
                  name="telepon"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.telepon ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("telepon", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Country"
                  name="negara"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.negara ||
                    "Indonesia"
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled || loading.countries}
                  options={[{ value: "", label: "Select Country" }, ...countries]}
                  error={getError("negara", currentParticipantIndex)}
                  aria-busy={loading.countries}
                  labelColor={labelColor}
                />
                <InputField
                  label="Province"
                  name="provinsi"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.provinsi ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={
                    isFormDisabled ||
                    !formData.participants[currentParticipantIndex]?.negara ||
                    loading.provinces
                  }
                  options={[
                    { value: "", label: "Select Province" },
                    ...participantProvinces[currentParticipantIndex],
                  ]}
                  error={getError("provinsi", currentParticipantIndex)}
                  aria-busy={loading.provinces}
                  labelColor={labelColor}
                />
                <InputField
                  label="City"
                  name="kota"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.kota || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={
                    isFormDisabled ||
                    !formData.participants[currentParticipantIndex]?.provinsi ||
                    loading.cities
                  }
                  options={[
                    { value: "", label: "Select City" },
                    ...participantCities[currentParticipantIndex],
                  ]}
                  error={getError("kota", currentParticipantIndex)}
                  aria-busy={loading.cities}
                  labelColor={labelColor}
                />
                <InputField
                  label="Address"
                  name="alamat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.alamat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("alamat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Emergency Contact Number"
                  name="kontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.kontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("kontakDarurat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Emergency Contact Name"
                  name="namaKontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.namaKontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaKontakDarurat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Relationship with Emergency Contact"
                  name="hubunganKontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.hubunganKontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError(
                    "hubunganKontakDarurat",
                    currentParticipantIndex
                  )}
                  labelColor={labelColor}
                />
                <InputField
                  label="ITRA Performance Index"
                  name="performanceIndex"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.performanceIndex || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("performanceIndex", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder="Example: 460"
                />
                <InputField
                  label="STRAVA Profile Link or similar"
                  name="stravaProfile"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.stravaProfile || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("stravaProfile", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder="Example: https://www.strava.com/athletes/72528358"
                />
              </div>
            </motion.div>
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={handlePreviousParticipant}
                className={`py-2 px-4 rounded-xl ${
                  currentParticipantIndex === 0
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-lime-600 hover:bg-lime-700 text-white"
                }`}
                disabled={currentParticipantIndex === 0 || isFormDisabled}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={handleNextParticipant}
                className={`py-2 px-4 rounded-xl ${
                  isNextDisabled()
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-lime-600 hover:bg-lime-700 text-white"
                }`}
                disabled={isNextDisabled() || isFormDisabled}
              >
                {currentParticipantIndex < 2 ? "Next" : "Finish"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <InputField
            label="First Name"
            name="namaDepan"
            value={formData.namaDepan || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaDepan")}
            labelColor={labelColor}
          />
          <InputField
            label="Last Name"
            name="namaBelakang"
            value={formData.namaBelakang || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaBelakang")}
            labelColor={labelColor}
          />
          {eventId === "JOTR" && (
            <InputField
              label="BIB Name"
              name="bibName"
              value={formData.bibName || ""}
              onChange={(e) => handleFieldChange(e)}
              disabled={isFormDisabled}
              required
              error={getError("bibName")}
              labelColor={labelColor}
            />
          )}
          {eventId === "JOTR" && (
            <div className="relative">
              <InputField
                label="BIB Number (Optional)"
                name="bibNumber"
                value={formData.bibNumber || ""}
                onChange={(e) => handleFieldChange(e)}
                onBlur={(e) => handleBibNumberBlur(e)}
                disabled={isFormDisabled}
                error={getError("bibNumber")}
                labelColor={labelColor}
                placeholder="Enter 1-4 digits, e.g., 1234"
                maxLength="4"
                aria-busy={loading.bibNumber}
              />
              {loading.bibNumber && (
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8 8 8 0 01-8-8z"
                    />
                  </svg>
                </span>
              )}
            </div>
          )}
          <InputField
            label="Identity Number"
            name="nomorIdentitas"
            value={formData.nomorIdentitas || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaDepan")}
            labelColor={labelColor}
            placeholder={
              formData.negara === "Indonesia"
                ? "Enter 16-digit ID Card (KTP)"
                : "Enter Passport/ID Number"
            }
          />
          <InputField
            label="Date of Birth"
            name="tanggalLahir"
            type="date"
            value={formData.tanggalLahir || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("tanggalLahir")}
            labelColor={labelColor}
          />
          {eventId === "JOTR" && (
            <InputField
              label="Blood Type"
              name="golonganDarah"
              type="select"
              value={formData.golonganDarah || ""}
              onChange={(e) => handleFieldChange(e)}
              disabled={isFormDisabled}
              required
              options={[
                { value: "", label: "Select Blood Type" },
                { value: "A", label: "A" },
                { value: "B", label: "B" },
                { value: "AB", label: "AB" },
                { value: "O", label: "O" },
              ]}
              error={getError("golonganDarah")}
              labelColor={labelColor}
            />
          )}
          {eventId === "JOTR" && (
            <div className="relative">
              <InputField
                label="Jersey Size"
                name="sizeJersey"
                type="select"
                value={formData.sizeJersey || ""}
                onChange={(e) => handleFieldChange(e)}
                disabled={isFormDisabled}
                required
                options={[
                  { value: "", label: "Select Jersey Size" },
                  { value: "XS", label: "XS" },
                  { value: "S", label: "S" },
                  { value: "M", label: "M" },
                  { value: "L", label: "L" },
                  { value: "XL", label: "XL" },
                  { value: "XXL", label: "XXL" },
                ]}
                error={getError("sizeJersey")}
                labelColor={labelColor}
              />
              <button
                type="button"
                onClick={openModal}
                className="absolute top-0 right-0 mt-2 mr-2 text-lime-600 hover:text-lime-800 text-sm underline"
                disabled={isFormDisabled}
              >
                View Size Chart
              </button>
            </div>
          )}
          <InputField
            label="Gender"
            name="gender"
            type="select"
            value={formData.gender || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("gender")}
            labelColor={labelColor}
            options={[
              { value: "", label: "Select Gender" },
              { value: "Laki-laki", label: "Male" },
              { value: "Perempuan", label: "Female" },
            ]}
          />
          <InputField
            label="Phone Number/WhatsApp"
            name="telepon"
            value={formData.telepon || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("telepon")}
            labelColor={labelColor}
          />
          <InputField
            label="Country"
            name="negara"
            type="select"
            value={formData.negara || "Indonesia"}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || loading.countries}
            options={[{ value: "", label: "Select Country" }, ...countries]}
            required
            error={getError("negara")}
            aria-busy={loading.countries}
            labelColor={labelColor}
          />
          <InputField
            label="Province"
            name="provinsi"
            type="select"
            value={formData.provinsi || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || !formData.negara || loading.provinces}
            options={[{ value: "", label: "Select Province" }, ...provinces]}
            required
            error={getError("provinsi")}
            aria-busy={loading.provinces}
            labelColor={labelColor}
          />
          <InputField
            label="City"
            name="kota"
            type="select"
            value={formData.kota || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || !formData.provinsi || loading.cities}
            options={[{ value: "", label: "Select City" }, ...cities]}
            required
            error={getError("kota")}
            aria-busy={loading.cities}
            labelColor={labelColor}
          />
          <InputField
            label="Address"
            name="alamat"
            value={formData.alamat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("alamat")}
            labelColor={labelColor}
          />
          <InputField
            label="Emergency Contact Number"
            name="kontakDarurat"
            value={formData.kontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("kontakDarurat")}
            labelColor={labelColor}
          />
          <InputField
            label="Emergency Contact Name"
            name="namaKontakDarurat"
            value={formData.namaKontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaKontakDarahat")}
            labelColor={labelColor}
          />
          <InputField
            label="Relationship with Emergency Contact"
            name="hubunganKontakDarurat"
            value={formData.hubunganKontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("hubunganKontakDarurat")}
            labelColor={labelColor}
          />
          {eventId === "JOTR" && (
            <>
              <InputField
                label="ITRA Performance Index"
                name="performanceIndex"
                value={formData.performanceIndex || ""}
                onChange={(e) => handleFieldChange(e)}
                disabled={isFormDisabled}
                required
                error={getError("performanceIndex")}
                labelColor={labelColor}
                placeholder="Example: 460"
              />
              <InputField
                label="STRAVA Profile Link or similar"
                name="stravaProfile"
                value={formData.stravaProfile || ""}
                onChange={(e) => handleFieldChange(e)}
                disabled={isFormDisabled}
                required
                error={getError("stravaProfile")}
                labelColor={labelColor}
                placeholder="Example: https://www.strava.com/athletes/72528358"
              />
              <InputField
                label="Category"
                name="kategori"
                type="select"
                value={formData.kategori || ""}
                onChange={(e) => handleFieldChange(e)}
                disabled={
                  isFormDisabled || isGroupRegistration || loading.categories
                }
                required
                options={[
                  { value: "", label: "Select Category" },
                  ...localCategories.map((cat) => ({
                    value: cat.categoryName,
                    label: cat.categoryName,
                  })),
                ]}
                error={getError("kategori")}
                labelColor={labelColor}
                aria-busy={loading.categories}
              />
            </>
          )}
        </>
      )}

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={sizeChartUrl}
                alt="Size Chart"
                className="w-full h-auto"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
